import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from 'features/common/hooks/useColumnSelection';
import { CompedTicketsTableProps } from '../types';
import {
	PunchupTableContainer,
	PunchupHeaderRow,
	PunchupColumnHeader,
	PunchupActionsHeaderCell,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupLoadMore } from 'features/common/components/Punchup/composites/tables/PunchupLoadMore';
import { PunchupCompedTicketsTableBody } from './PunchupCompedTicketsTableBody';

interface PunchupCompedTicketsTableProps extends CompedTicketsTableProps {
	visibleDefs: ColumnDef[];
	gridColumns: string;
	onResizeColumn: (key: string, width: number) => void;
	/** Render the sticky actions column (header + cells). Must match the
	 * `hasActionsColumn` given to `useColumnSelection` so cells and grid tracks agree. */
	hasActionsColumn: boolean;
	/** True while the initial fetch is in flight (data === undefined). */
	isLoading?: boolean;
	/** True while a next-page fetch is in flight (paginating). */
	isFetchingNextPage?: boolean;
	/** True while an already-loaded table re-fetches — dims the table with a spinner overlay. */
	isRefetching?: boolean;
	/** True when a search term or non-default filter is active — switches the empty body to "no results" copy. */
	hasActiveQuery?: boolean;
}

/**
 * Table + load-more for the Comped Tickets sub-page. Returns a Fragment so
 * the parent page can wrap this and the controls row inside a single
 * `PunchupTableLayout`, which keeps the right edge of the controls aligned
 * with the right edge of the table on wide viewports.
 */
export const PunchupCompedTicketsTable: FC<PunchupCompedTicketsTableProps> = ({
	compedTicketsQueryObject,
	openCancelModal,
	visibleDefs,
	gridColumns,
	onResizeColumn,
	hasActionsColumn,
	isLoading,
	isFetchingNextPage,
	isRefetching,
	hasActiveQuery,
}) => {
	const { t } = useTranslation();
	const { fetchNextPage, hasNextPage } = compedTicketsQueryObject;

	return (
		<>
			<PunchupTableContainer columns={gridColumns} isRefetching={isRefetching}>
				<thead>
					<PunchupHeaderRow>
						{visibleDefs.map((col) => (
							<PunchupColumnHeader key={col.key} onResize={(w) => onResizeColumn(col.key, w)}>
								{t(col.labelKey)}
							</PunchupColumnHeader>
						))}
						{hasActionsColumn && <PunchupActionsHeaderCell />}
					</PunchupHeaderRow>
				</thead>
				<PunchupCompedTicketsTableBody
					compedTicketsQueryObject={compedTicketsQueryObject}
					openCancelModal={openCancelModal}
					visibleDefs={visibleDefs}
					hasActionsColumn={hasActionsColumn}
					isLoading={isLoading}
					hasActiveQuery={hasActiveQuery}
				/>
			</PunchupTableContainer>

			<PunchupLoadMore isDisabled={!hasNextPage} isLoading={isFetchingNextPage} handleClick={() => fetchNextPage()} />
		</>
	);
};
